import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  deleteFactoringNominee,
  deleteFactoringNomineePreview,
} from 'lib/api/reSell/factoring';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface DeleteNomineeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  nomineeId: string | number;
  onDeleteSuccess: () => void;
}

const DeleteNomineeModal: React.FC<DeleteNomineeModalProps> = ({
  open,
  setOpen,
  nomineeId,
  onDeleteSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [previewDetails, setPreviewDetails] = useState<{
    totalResellsCount: number;
    companyResellsCount: number;
    resellsWithRestrictions: Array<{
      resellId: number;
      hasFundPayments: boolean;
      hasPaymentNotifications: boolean;
      fundPaymentsCount: number;
      paymentNotificationsCount: number;
    }>;
    conciliationsCount: number;
  } | null>(null);
  const dispatch = useDispatch();

  const checkIfCanDelete = async () => {
    setPreviewLoading(true);
    setPreviewDetails(null);
    try {
      const response = await deleteFactoringNomineePreview(dispatch, nomineeId);
      if (response) {
        setCanDelete(response.canDelete);
        setPreviewDetails(response.details);
      }
    } catch (error) {
      setCanDelete(false);
      showAlert(
        dispatch,
        'error',
        'Error al verificar si se puede eliminar la nómina',
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void checkIfCanDelete();
      setAgreeTerms(false);
    }
  }, [open]);

  const handleDeleteNominee = async () => {
    setLoading(true);
    try {
      await deleteFactoringNominee(dispatch, nomineeId);
      showAlert(
        dispatch,
        'success',
        'La nómina ha sido eliminada exitosamente.',
      );
      setOpen(false);
      onDeleteSuccess();
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar eliminar la nómina.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !previewLoading) {
      setOpen(false);
      setAgreeTerms(false);
      setCanDelete(false);
      setPreviewDetails(null);
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {!canDelete && previewDetails ? (
              <ErrorOutlineIcon color="error" fontSize="medium" />
            ) : (
              <WarningAmberIcon color="warning" fontSize="medium" />
            )}
            <Typography variant="h6" fontWeight={600}>
              Eliminar nómina #{nomineeId}
            </Typography>
          </Stack>
        </Box>
      </DialogTitle>
      {loading || previewLoading ? (
        <DialogContent sx={{ minHeight: 200 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            {previewDetails && (
              <>
                {!canDelete ? (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'error.lighter',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'error.light',
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="error.dark"
                        sx={{ mb: 1 }}
                      >
                        No se puede eliminar esta nómina
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Existen restricciones que impiden la eliminación
                      </Typography>
                    </Box>

                    {previewDetails.resellsWithRestrictions.length > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}
                        >
                          Reventas con restricciones
                        </Typography>
                        <Stack spacing={1.5}>
                          {previewDetails.resellsWithRestrictions.map(
                            (resell, index) => (
                              <Box
                                key={index}
                                sx={{
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ minWidth: 100 }}
                                  >
                                    Reventa #{resell.resellId}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flex={1}>
                                    {resell.hasFundPayments && (
                                      <Chip
                                        label={`${resell.fundPaymentsCount} pago(s) fondo`}
                                        color="error"
                                        size="small"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    )}
                                    {resell.hasPaymentNotifications && (
                                      <Chip
                                        label={`${resell.paymentNotificationsCount} aviso(s) pago`}
                                        color="error"
                                        size="small"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                              </Box>
                            ),
                          )}
                        </Stack>
                      </Box>
                    )}

                    {previewDetails.conciliationsCount > 0 && (
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Conciliaciones asociadas:
                          </Typography>
                          <Chip
                            label={`${previewDetails.conciliationsCount} conciliación(es)`}
                            color="error"
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'warning.lighter',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.light',
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="warning.dark"
                        sx={{ mb: 0.5 }}
                      >
                        Se eliminarán todas las dependencias
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Esta acción no se puede deshacer
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}
                      >
                        Resumen de eliminación
                      </Typography>
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Facturas asociadas
                          </Typography>
                          <Chip
                            label={previewDetails.totalResellsCount}
                            color="default"
                            size="small"
                            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                          />
                        </Stack>
                        {previewDetails.conciliationsCount > 0 && (
                          <>
                            <Divider />
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Conciliaciones asociadas
                              </Typography>
                              <Chip
                                label={previewDetails.conciliationsCount}
                                color="default"
                                size="small"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'info.lighter',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.light',
                        mt: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={() => setAgreeTerms(!agreeTerms)}
                            checked={agreeTerms}
                            sx={{ py: 0 }}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            Confirmo que entiendo las consecuencias de esta
                            acción
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Stack>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <CustomButton
              onClick={handleClose}
              color="secondary"
              disabled={loading}
            >
              Cancelar
            </CustomButton>
            {canDelete && (
              <CustomButton
                onClick={handleDeleteNominee}
                color="error"
                disabled={!agreeTerms || loading}
              >
                Eliminar
              </CustomButton>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DeleteNomineeModal;
